import React, { useState } from 'react';
import { Button, Modal, DatePicker, Alert, Space, Typography, Table, Checkbox, message, Statistic, Row, Col } from 'antd';
import { HistoryOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { recalculateProductStock, RecalcResult } from '../../api/reportApi';

const { Text, Paragraph } = Typography;

type RestoreStockButtonProps = {
    // Called after stock is actually written, so the caller can refresh.
    onSuccess?: () => void;
};

// The day the stock was wiped by reset-stock. Anything earlier than this is
// only counted when "count the entire history" is ticked.
const RESET_DAY = '2026-07-11';

const RestoreStockButton: React.FC<RestoreStockButtonProps> = ({ onSuccess }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [from, setFrom] = useState<dayjs.Dayjs | null>(dayjs(RESET_DAY));
    const [to, setTo] = useState<dayjs.Dayjs | null>(dayjs());
    const [allHistory, setAllHistory] = useState(false);
    const [preview, setPreview] = useState<RecalcResult | null>(null);
    const [loading, setLoading] = useState(false);

    const params = (confirm: boolean) => ({
        from: allHistory ? null : from?.format('YYYY-MM-DD') ?? null,
        to: to?.format('YYYY-MM-DD') ?? null,
        confirm,
    });

    const apiError = (err: any, fallback: string) =>
        err?.response?.data?.message || err?.response?.data?.error || fallback;

    const handlePreview = async () => {
        setLoading(true);
        try {
            setPreview(await recalculateProductStock(params(false)));
        } catch (err: any) {
            message.error(apiError(err, t('failed_to_recalculate_stock') || 'Failed to preview'));
        } finally {
            setLoading(false);
        }
    };

    const handleApply = async () => {
        setLoading(true);
        try {
            const result = await recalculateProductStock(params(true));
            message.success(
                t('stock_recalculated_successfully') ||
                `Stock re-counted for ${result.productsChanged} products.`
            );
            setOpen(false);
            setPreview(null);
            onSuccess?.();
        } catch (err: any) {
            message.error(apiError(err, t('failed_to_recalculate_stock') || 'Failed to recalculate stock'));
        } finally {
            setLoading(false);
        }
    };

    // Any change to the inputs invalidates the preview — never let someone
    // apply a result that was computed for a different date range.
    const invalidate = () => setPreview(null);

    const rows = (preview?.changes || []).flatMap(change =>
        Object.entries(change.units).map(([unit, diff]) => ({
            key: `${change.id}-${unit}`,
            code: change.code,
            name: change.name,
            unit,
            ...diff,
        }))
    );

    return (
        <>
            <Button icon={<HistoryOutlined />} onClick={() => setOpen(true)}>
                {t('restore_stock') || 'Restore Stock'}
            </Button>
            <Modal
                title={
                    <Space>
                        <HistoryOutlined />
                        {t('restore_stock') || 'Restore Stock from invoice history'}
                    </Space>
                }
                open={open}
                width={760}
                onCancel={() => { setOpen(false); setPreview(null); }}
                destroyOnClose
                footer={[
                    <Button key="cancel" onClick={() => { setOpen(false); setPreview(null); }}>
                        {t('cancel') || 'Cancel'}
                    </Button>,
                    <Button key="preview" onClick={handlePreview} loading={loading}>
                        {t('preview_changes') || 'Preview'}
                    </Button>,
                    <Button
                        key="apply"
                        type="primary"
                        danger
                        loading={loading}
                        disabled={!preview || preview.productsChanged === 0}
                        onClick={handleApply}
                    >
                        {t('apply_restore') || 'Apply'}
                    </Button>,
                ]}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                        type="info"
                        showIcon
                        message={t('restore_stock_scope_title') || 'What this restores'}
                        description={
                            <Paragraph style={{ marginBottom: 0 }}>
                                {t('restore_stock_scope') ||
                                    'Stock is re-counted from purchase, sale and return invoices, which are intact. ' +
                                    'Quantities typed directly into the product form were erased by the stock reset ' +
                                    'and cannot be recovered here — those need a manual count.'}
                            </Paragraph>
                        }
                    />

                    <Row gutter={16}>
                        <Col span={12}>
                            <Text strong>{t('count_stock_from') || 'Count stock from'}</Text>
                            <DatePicker
                                value={from}
                                onChange={(d) => { setFrom(d); invalidate(); }}
                                format="YYYY-MM-DD"
                                disabled={allHistory}
                                style={{ width: '100%', marginTop: 8 }}
                            />
                        </Col>
                        <Col span={12}>
                            <Text strong>{t('until') || 'Until'}</Text>
                            <DatePicker
                                value={to}
                                onChange={(d) => { setTo(d); invalidate(); }}
                                format="YYYY-MM-DD"
                                style={{ width: '100%', marginTop: 8 }}
                            />
                        </Col>
                    </Row>

                    <Checkbox
                        checked={allHistory}
                        onChange={(e) => { setAllHistory(e.target.checked); invalidate(); }}
                    >
                        {t('count_entire_history') ||
                            'Count the entire invoice history (ignore the reset baseline)'}
                    </Checkbox>

                    {preview && (
                        <>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Statistic
                                        title={t('products_changed') || 'Products changed'}
                                        value={preview.productsChanged}
                                        suffix={`/ ${preview.productsTotal}`}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic title={t('units_changed') || 'Units changed'} value={preview.unitsChanged} />
                                </Col>
                            </Row>

                            {preview.productsChanged === 0 ? (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message={t('restore_no_change_title') || 'This range changes nothing'}
                                    description={
                                        t('restore_no_change') ||
                                        'Stock is already counted from this date, so applying it would have no effect. ' +
                                        'Tick "count the entire invoice history" to pull in invoices from before the reset.'
                                    }
                                />
                            ) : (
                                <>
                                    <Alert
                                        type="warning"
                                        showIcon
                                        icon={<WarningOutlined />}
                                        message={t('restore_preview_notice') || 'Nothing has been saved yet'}
                                        description={
                                            t('restore_preview_notice_desc') ||
                                            'This is a preview. Current stock is snapshotted before anything is written, so it can be undone.'
                                        }
                                    />
                                    <Table
                                        size="small"
                                        dataSource={rows}
                                        pagination={{ pageSize: 8, size: 'small' }}
                                        scroll={{ x: true }}
                                        columns={[
                                            { title: t('code') || 'Code', dataIndex: 'code', width: 90 },
                                            { title: t('name') || 'Name', dataIndex: 'name', ellipsis: true },
                                            { title: t('unit') || 'Unit', dataIndex: 'unit', width: 90 },
                                            { title: t('before') || 'Before', dataIndex: 'before', width: 80 },
                                            { title: t('after') || 'After', dataIndex: 'after', width: 80 },
                                            {
                                                title: t('change') || 'Change',
                                                dataIndex: 'delta',
                                                width: 90,
                                                render: (delta: number) => (
                                                    <Text type={delta > 0 ? 'success' : 'danger'}>
                                                        {delta > 0 ? `+${delta}` : delta}
                                                    </Text>
                                                ),
                                            },
                                        ]}
                                    />
                                </>
                            )}
                        </>
                    )}
                </Space>
            </Modal>
        </>
    );
};

export default RestoreStockButton;
